// ─── I suggerimenti e i problemi dei soci ────────────────────────────────────
//
// 💡 24/09/2026, dal mockup approvato. Il socio li scrive dal bot; la
// segreteria li legge nel gestionale (Telegram → Segnalazioni dei soci) e può
// rispondere: la risposta arriva al socio su Telegram, con la coda degli
// avvisi (gesto `risposta`).
//
// 🤖 26/09/2026, dal mockup approvato («anomalia del prezzo»): in Segnalazioni
// scrive anche l'ASSISTENTE DIGITALE, quando trova qualcosa che non torna — la
// prima è l'ora libera che il listino non sa quotare. Un'anomalia non ha un
// socio, e quindi non ha una risposta.
//
// ✉️ 27/09/2026, dal mockup approvato («Scrivi alla Segreteria»): il bot non
// dà più il numero della Segreteria. Nasce la `domanda`, accanto a problema e
// suggerimento, e il socio può rispondere alla risposta (`inReplyTo`: il filo).
import { z } from 'zod';
import { ARGOMENTI_DEL_CIRCOLO } from './conoscenza.js';
import { giornoPerEsteso } from './avvisi.js';
import { dataSchema, oraSchema } from './circolo.js';
export const TIPI_SUGGERIMENTO = ['suggerimento', 'problema', 'domanda'];
export const tipoSuggerimentoSchema = z.enum(TIPI_SUGGERIMENTO);
/**
 * 🤖 Quello che il bot scrive PER un socio (voce 31, passo 2 — 27/09/2026, mockup
 * approvato): una domanda a cui l'assistente non ha saputo rispondere, e una
 * prenotazione rimasta senza verdetto dopo l'attesa. Hanno un socio, e gli si
 * risponde come agli altri messaggi.
 * 📱 29/09/2026 (B2, mockup «I miei dati», scelta 2A): il numero che il socio
 * voleva e che sta sulla scheda di un altro socio. Lo scrive il gestionale
 * stesso, quando il ponte non l'ha cambiato.
 * 🛡️ 22/10/2026 (mockup «La cancellazione chiesta dal socio», A): il socio chiede di cancellare i suoi dati,
 * da «📝 I miei dati». La cancellazione la fa il titolare, nella scheda.
 */
export const TIPI_DAL_BOT = [
    'senza_risposta',
    'senza_esito',
    'numero_occupato',
    'cancellazione_dati',
];
/**
 * ⭐ Cosa può stare in Segnalazioni: quello che scrive il socio, quello che il bot
 * scrive per lui, e l'anomalia dell'assistente digitale.
 */
export const TIPI_SEGNALAZIONE = [
    ...TIPI_SUGGERIMENTO,
    ...TIPI_DAL_BOT,
    'anomalia',
    'promoter',
    'livello_da_vedere',
];
export const tipoSegnalazioneSchema = z.enum(TIPI_SEGNALAZIONE);
export const ETICHETTE_TIPO_SUGGERIMENTO = {
    suggerimento: 'Suggerimento',
    problema: 'Problema',
    domanda: 'Domanda',
    senza_risposta: 'Senza risposta',
    senza_esito: 'Senza esito',
    numero_occupato: 'Numero da sistemare',
    cancellazione_dati: 'Cancellazione dei dati',
    anomalia: 'Anomalia',
    // 🎾 16/10/2026: un promoter sotto il minimo di partite da 2 settimane di fila.
    promoter: 'Promoter',
    // 📊 23/10/2026: le domande del livello non sono bastate due volte di fila; il bot ha segnato il più basso.
    livello_da_vedere: 'Livello da vedere in campo',
};
/** 📊 Le segnalazioni per la Segreteria, che non si rispondono: portano alla scheda del socio. */
export function soloPerLaSegreteria(tipo) {
    return tipo === 'anomalia' || tipo === 'promoter' || tipo === 'livello_da_vedere';
}
/** 🤖 Il tipo è scritto dal bot per il socio? */
export function scrittoDalBot(tipo) {
    return TIPI_DAL_BOT.includes(tipo);
}
/**
 * 🤖 La casella del listino che manca: dove scrivere il prezzo. Con questa il
 * bottone «Metti il prezzo che manca» porta sulla tabella giusta, con la
 * casella vuota evidenziata.
 */
export const casellaMancanteSchema = z.object({
    listinoId: z.uuid(),
    tabellaId: z.uuid(),
    weekday: z.number().int().min(0).max(6),
    time: z.string().regex(/^\d{2}:\d{2}$/),
});
/** 📏 Duemila caratteri: un messaggio di Telegram ne tiene quattromila, ma questo si legge da una pagina. */
export const testoSuggerimentoSchema = z
    .string()
    .trim()
    .min(1, 'Il messaggio è vuoto')
    .max(2000, 'Il messaggio è troppo lungo: al massimo 2000 caratteri');
// ── Dal bot (ponte) ──────────────────────────────────────────────────────────
/**
 * 🔑 `idempotencyKey` la sceglie il bot: se non sa se il messaggio è arrivato
 * e lo rimanda, arriva una volta sola.
 * ⚠️ Il testo si controlla nella rotta, non qui: un testo che non va torna
 * `rifiutato` con la frase per il socio, non come `400`.
 */
export const suggerimentoDalPonteSchema = z.object({
    kind: tipoSuggerimentoSchema,
    text: z.string().max(10_000),
    idempotencyKey: z.string().min(8).max(100),
    /** ✉️ Il messaggio a cui il socio risponde: la risposta che la Segreteria gli ha mandato. */
    inReplyTo: z.uuid().optional(),
});
// ── 🤖 Il bot segnala da sé (voce 31, passo 2) ─────────────────────────────────
/** La prenotazione rimasta senza verdetto, com'era stata chiesta. */
export const richiestaSenzaEsitoSchema = z.object({
    date: dataSchema,
    time: oraSchema,
    courtName: z.string().min(1).max(60),
    durationMinutes: z.number().int().min(15).max(600),
});
/**
 * 🔑 `idempotencyKey` la sceglie il bot, come per i messaggi del socio. Per
 * `senza_esito`, `bookingKey` è la chiave con cui il bot aveva chiesto la
 * prenotazione: il gestionale la usa per dire se la prenotazione c'è.
 */
export const segnalazioneDelBotSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('senza_risposta'),
        question: z.string().trim().min(1).max(2000),
        botReply: z.string().max(2000),
        idempotencyKey: z.string().min(8).max(100),
    }),
    z.object({
        kind: z.literal('senza_esito'),
        request: richiestaSenzaEsitoSchema,
        bookingKey: z.string().min(8).max(200),
        botReply: z.string().max(2000),
        idempotencyKey: z.string().min(8).max(100),
    }),
]);
/** «Lascia stare, non serve»: il socio ritira la domanda che il bot aveva girato. */
export const ritiroDelBotSchema = z.object({ idempotencyKey: z.string().min(8).max(100) });
/** 📏 «Sabato 4 ottobre alle 18:00 · Campo 2 · 90 minuti»: la richiesta, come la legge il socio. */
export function cosaDellaRichiesta(r) {
    const g = giornoPerEsteso(r.date);
    return `${g.charAt(0).toUpperCase()}${g.slice(1)} alle ${r.time} · ${r.courtName} · ${r.durationMinutes} minuti`;
}
/** 🤖 Le domande che lo stesso socio fa entro questi minuti finiscono in una segnalazione sola (scelta 4 A). */
export const MINUTI_PER_ACCODARE = 10;
export const esitoSuggerimentoSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato']),
    reason: z.string().nullable(),
    id: z.uuid().nullable(),
});
// ── Per la segreteria ────────────────────────────────────────────────────────
export const FILTRI_SUGGERIMENTI = ['da_leggere', 'letti', 'tutti'];
export const domandaSuggerimentiSchema = z.object({
    filtro: z.enum(FILTRI_SUGGERIMENTI).default('da_leggere'),
});
export const suggerimentoSchema = z.object({
    id: z.uuid(),
    /** Nullo per un'anomalia: la scrive l'assistente digitale, non un socio. */
    memberId: z.uuid().nullable(),
    memberName: z.string().nullable(),
    kind: tipoSegnalazioneSchema,
    text: z.string(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
    reply: z.object({ text: z.string(), at: z.string(), by: z.string().nullable() }).nullable(),
    /** 🤖 Per l'anomalia del prezzo: la casella da riempire, se si sa qual è. */
    missingCell: casellaMancanteSchema.nullable().default(null),
    /** ✉️ «gioca oggi alle 18:00»: l'ora della sua prossima partita di oggi, se ce n'è una. */
    giocaOggi: z.string().nullable().default(null),
    /** ✉️ Lo scambio a cui il socio risponde: cosa aveva scritto, e cosa gli avete risposto. */
    filo: z.object({ del: z.string(), scritto: z.string(), risposta: z.string() }).nullable().default(null),
    /** 🤖 Cosa il bot ha detto al socio. */
    botReply: z.string().nullable().default(null),
    /** 🤖 Le domande che il socio ha fatto dopo la prima, nei 10 minuti: una sotto l'altra. */
    altre: z.array(z.string()).default([]),
    /** 🤖 Per `senza_esito`: la prenotazione chiesta, e se nel gestionale c'è. */
    richiesta: richiestaSenzaEsitoSchema.nullable().default(null),
    /**
     * 📱 Per `numero_occupato` (B2, 29/09/2026): il numero che il socio ha
     * condiviso, e la scheda su cui sta già. Nullo per gli altri tipi.
     */
    numero: z
        .object({ phone: z.string(), altroId: z.uuid(), altroNome: z.string().nullable() })
        .nullable()
        .default(null),
    trovata: z
        .object({
        stato: z.enum(['c-e', 'annullata', 'non-c-e']),
        /** «17:42»: quando è nata, nel fuso del circolo. Nullo se non c'è. */
        natoAlle: z.string().nullable(),
        /** Per aprirla nel calendario. Nullo se non c'è. */
        bookingId: z.uuid().nullable(),
    })
        .nullable()
        .default(null),
});
export const elencoSuggerimentiSchema = z.object({
    items: z.array(suggerimentoSchema),
    /** Quanti ce ne sono ancora da leggere, qualunque sia il filtro: il numero accanto alla voce del menu. */
    daLeggere: z.number().int(),
});
export const contoSuggerimentiSchema = z.object({ daLeggere: z.number().int() });
export const rispostaSuggerimentoSchema = z.object({
    text: z.string().trim().min(1, 'La risposta è vuota').max(1000, 'Al massimo 1000 caratteri'),
});
// ─── ✨ Consulta AI (mockup approvato il 26/09/2026) ─────────────────────────
//
// 🗣️ *«Consulta AI … spingendo quel bottone io guardo la risposta che darebbe
// l'intelligenza artificiale e poi decido se utilizzarla oppure no.»*
// ⇒ Niente di quello che torna da qui parte verso il socio: è una proposta per
//   la Segreteria, che la usa, la corregge o la lascia.
/** Dove l'AI ha trovato la risposta, in «Cosa dice il bot ai soci». */
export const FONTI_DELLA_PROPOSTA = [
    { chiave: 'segreteria', titolo: 'La segreteria' },
    { chiave: 'regole', titolo: 'Le regole' },
    ...ARGOMENTI_DEL_CIRCOLO.map((a) => ({ chiave: a.chiave, titolo: a.titolo })),
];
/**
 * ⚖️ Quattro esiti, e nessuno è un errore: «non lo so», «non è collegata» e
 * «non ha risposto» sono risposte che la pagina mostra nel riquadro, non
 * guasti. Un guasto direbbe «rifalla», e rifarla spende di nuovo.
 */
export const STATI_DELLA_PROPOSTA = ['proposta', 'non-sa', 'spenta', 'non-risponde'];
export const propostaAiSchema = z.object({
    stato: z.enum(STATI_DELLA_PROPOSTA),
    /** Il testo da mostrare nel riquadro: la risposta proposta, oppure il perché non c'è. */
    testo: z.string(),
    /** Il titolo dell'argomento da cui l'ha presa; `null` se non l'ha presa da niente. */
    fonte: z.string().nullable(),
});
export const STATI_DELLA_REGOLA = ['proposta', 'gia-c-e', 'spenta', 'non-risponde'];
export const argomentoDellaRegolaSchema = z.enum(ARGOMENTI_DEL_CIRCOLO.map((a) => a.chiave));
export const regolaAiSchema = z.object({
    stato: z.enum(STATI_DELLA_REGOLA),
    argomento: argomentoDellaRegolaSchema.nullable(),
    /** Il titolo dell'argomento, com'è scritto nella pagina. */
    titolo: z.string().nullable(),
    testo: z.string(),
});
/**
 * 🟢 Se «Consulta AI» funziona, visto senza consultare niente (27/09/2026,
 * mockup approvato, strada B): la riga in cima a Segnalazioni, e il bottone
 * spento quando non è `collegata`. Non porta né il modello né la chiave.
 */
export const STATI_DELL_AI = ['collegata', 'non_risponde', 'non_collegata'];
export const statoAiSchema = z.object({
    stato: z.enum(STATI_DELL_AI),
    /** Quando il gestionale l'ha controllata; `null` se non c'era niente da controllare (manca la chiave). */
    controllatoAlle: z.string().nullable(),
});
/** La domanda per la regola: la risposta che la Segreteria ha mandato è già salvata. */
export const aggiungiRegolaSchema = z.object({
    argomento: argomentoDellaRegolaSchema,
    testo: z.string().trim().min(1, 'La regola è vuota').max(600, 'Al massimo 600 caratteri'),
});
//# sourceMappingURL=suggerimenti.js.map